import { fuelKindForType } from "./catalogs";
import { convertFuel, fuelUsedToUsGal, type FuelUnit } from "./fuelUnits";
import type { Mission, MissionOfpAttachment, OfpFuelPlan } from "./types";

/** Default Hobbs pad when SimConnect has air time but no engine counter. */
export const HOBBS_PAD_HOURS = 0.2;
export const HOBBS_PAD_HOURS_MIN = 0.1;

export function clampHobbsPad(value: unknown, fallback = HOBBS_PAD_HOURS): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  if (value < HOBBS_PAD_HOURS_MIN) return HOBBS_PAD_HOURS_MIN;
  if (value > HOBBS_PAD_HOURS) return HOBBS_PAD_HOURS;
  return Math.round(value * 100) / 100;
}

/**
 * Engine hours for post-flight. Prefer a SimConnect taxi+air estimate when
 * present (still not a real Hobbs L-var). Otherwise air + pad.
 */
export function estimateEngineHours(opts: {
  airTimeHours?: number | null;
  simEngineHours?: number | null;
  hobbsPadHours?: number;
}): { engineHours: number | null; source: "simconnect" | "hobbs" | null } {
  const sim = opts.simEngineHours;
  if (sim != null && Number.isFinite(sim) && sim > 0) {
    return { engineHours: Math.round(sim * 100) / 100, source: "simconnect" };
  }
  const air = opts.airTimeHours;
  if (air == null || !Number.isFinite(air) || air <= 0) {
    return { engineHours: null, source: null };
  }
  const pad = clampHobbsPad(opts.hobbsPadHours);
  return { engineHours: Math.round((air + pad) * 100) / 100, source: "hobbs" };
}

export function ofpMassUnit(units?: string): "lb" | "kg" | null {
  const raw = (units ?? "").trim().toLowerCase();
  if (raw === "lbs" || raw === "lb") return "lb";
  if (raw === "kgs" || raw === "kg") return "kg";
  return null;
}

/** Ramp / takeoff planned fuel in the OFP's own mass unit. */
export function ofpPlannedFuelAmount(plan?: OfpFuelPlan | null): number | null {
  if (!plan) return null;
  const n = plan.planRamp ?? plan.planTakeoff ?? plan.minTakeoff ?? plan.trip;
  if (n == null || !Number.isFinite(n) || n < 0) return null;
  return n;
}

function massToTicketFuel(
  amount: number | null | undefined,
  units: string | undefined,
  icaoType?: string,
): { fuelGal: number; fuelLbs: number } | null {
  const mass = ofpMassUnit(units);
  if (amount == null || !Number.isFinite(amount) || amount < 0 || !mass) return null;
  const kind = fuelKindForType(icaoType);
  const fuelGal = Math.round(fuelUsedToUsGal(amount, mass, kind) * 10) / 10;
  const fuelLbs = Math.round(convertFuel(amount, mass, "lb", kind));
  if (!(fuelGal > 0) && !(fuelLbs > 0)) return null;
  return { fuelGal, fuelLbs };
}

export function ticketFuelFromOfp(
  attachment: Pick<MissionOfpAttachment, "fuelPlan">,
  icaoType?: string,
): { fuelGal: number; fuelLbs: number } | null {
  return massToTicketFuel(ofpPlannedFuelAmount(attachment.fuelPlan), attachment.fuelPlan?.units, icaoType);
}

/**
 * Stable planned numbers to persist on the OFP attach.
 * Analytics can compare these to actual sim / post-flight burn without
 * reading the editable ticket fuel fields.
 */
export function plannedFuelSnapshotFromOfp(
  attachment: Pick<MissionOfpAttachment, "fuelPlan" | "aircraftType">,
  icaoType?: string,
): Pick<MissionOfpAttachment, "plannedFuelGal" | "plannedFuelLbs" | "plannedTripGal" | "plannedTripLbs"> {
  const type = icaoType || attachment.aircraftType;
  const ramp = ticketFuelFromOfp(attachment, type);
  const trip = massToTicketFuel(attachment.fuelPlan?.trip, attachment.fuelPlan?.units, type);
  return {
    plannedFuelGal: ramp?.fuelGal,
    plannedFuelLbs: ramp?.fuelLbs,
    plannedTripGal: trip?.fuelGal,
    plannedTripLbs: trip?.fuelLbs,
  };
}

export function ofpPlannedFuelInUnit(
  attachment: Pick<MissionOfpAttachment, "fuelPlan">,
  unit: FuelUnit,
  icaoType?: string,
): number | null {
  const planned = ticketFuelFromOfp(attachment, icaoType);
  if (!planned) return null;
  const kind = fuelKindForType(icaoType);
  return Math.round(convertFuel(planned.fuelGal, "gal", unit, kind) * 10) / 10;
}

export function shouldSeedTicketFuel(mission: Pick<Mission, "ofpAttachment" | "ofpFuelTouched">): boolean {
  if (mission.ofpFuelTouched) return false;
  return !mission.ofpAttachment;
}

export type FuelPrefillSource = "user" | "simconnect" | "simbrief" | null;

export type SimFuelActual = {
  fuelUpGal?: number | null;
  fuelDownGal?: number | null;
  fuelUsedGal?: number | null;
};

export type FuelPrefill = {
  source: FuelPrefillSource;
  fuelUpGal?: number;
  fuelDownGal?: number;
  fuelUsedGal?: number;
};

function finiteFuel(value: number | null | undefined): number | undefined {
  if (value == null || !Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value * 10) / 10;
}

/**
 * Emerson doctrine: user edits win; SimConnect actuals beat SimBrief planned;
 * SimBrief is planned baseline + fallback. Never a locked value.
 */
export function resolveFuelPrefill(opts: {
  userEdited?: boolean;
  simActual?: SimFuelActual | null;
  ofpPlannedGal?: number | null;
}): FuelPrefill {
  if (opts.userEdited) return { source: "user" };
  const up = finiteFuel(opts.simActual?.fuelUpGal);
  const down = finiteFuel(opts.simActual?.fuelDownGal);
  const used = finiteFuel(opts.simActual?.fuelUsedGal);
  if (up != null || down != null || used != null) {
    return { source: "simconnect", fuelUpGal: up, fuelDownGal: down, fuelUsedGal: used };
  }
  const planned = finiteFuel(opts.ofpPlannedGal);
  if (planned != null) return { source: "simbrief", fuelUpGal: planned };
  return { source: null };
}

export function resolveTicketFuelPrefill(opts: {
  userEdited?: boolean;
  simActualGal?: number | null;
  ofpPlanned?: { fuelGal: number; fuelLbs: number } | null;
  icaoType?: string;
}): { source: FuelPrefillSource; fuelGal?: number; fuelLbs?: number } {
  if (opts.userEdited) return { source: "user" };
  const actual = finiteFuel(opts.simActualGal);
  if (actual != null) {
    const kind = fuelKindForType(opts.icaoType);
    return {
      source: "simconnect",
      fuelGal: actual,
      fuelLbs: Math.round(convertFuel(actual, "gal", "lb", kind)),
    };
  }
  if (opts.ofpPlanned && (opts.ofpPlanned.fuelGal > 0 || opts.ofpPlanned.fuelLbs > 0)) {
    return { source: "simbrief", fuelGal: opts.ofpPlanned.fuelGal, fuelLbs: opts.ofpPlanned.fuelLbs };
  }
  return { source: null };
}

export function applyOfpFuelToMission(
  mission: Mission,
  attachment: MissionOfpAttachment,
  opts: { overwrite?: boolean; simActualGal?: number | null; preferOfp?: boolean } = {},
): Mission {
  if (!opts.overwrite && mission.ofpFuelTouched) return mission;
  const ofpPlanned = ticketFuelFromOfp(attachment, attachment.aircraftType || mission.aircraftName);
  const picked = opts.preferOfp
    ? resolveTicketFuelPrefill({ ofpPlanned, icaoType: attachment.aircraftType })
    : resolveTicketFuelPrefill({
        simActualGal: opts.simActualGal,
        ofpPlanned,
        icaoType: attachment.aircraftType,
      });
  if (picked.source == null || picked.fuelGal == null || picked.fuelLbs == null) return { ...mission, ofpAttachment: attachment };
  const legs = mission.legs?.length
    ? mission.legs.map((leg, i) => (i === 0 ? { ...leg, fuelGal: picked.fuelGal!, fuelLbs: picked.fuelLbs! } : leg))
    : mission.legs;
  return {
    ...mission,
    fuelGal: picked.fuelGal,
    fuelLbs: picked.fuelLbs,
    legs,
    ofpAttachment: attachment,
  };
}
